// src/pages/HistoryTable.js

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Badge, Button, Flex, Text } from '@radix-ui/themes';
import { MdOutlineVerifiedUser } from 'react-icons/md';
import { BiFilterAlt } from 'react-icons/bi';
import TableComponent from '../components/ui-components/TableComponent';
import ViewAlarmHistory from '../components/ui-components/ViewAlarmHistory';
import HistoryFilter, { defaultHistoryFilters } from '../components/ui-components/HistoryFilter';
import PaginationComponent from '../components/ui-components/PaginationComponent';
import axiosInstance from '../config/axiosConfig';
import { buildUtcDateTimeFilterRange, formatApiDate, formatApiTime, getClientTimezoneOffsetMinutes } from '../utils/helpers';

const masterAlarmMapping = {
    1: 'Diaper',
    2: 'Wake Up',
    3: 'Nurse Call',
    4: 'Patient Rolling',
    5: 'F&B',
    6: 'Security',
    7: 'Housekeeping',
    8: 'Patient Out Of Room',
    9: 'Medication'
};

const statusOptions = ['Pending', 'Confirmed', 'Responded', 'Resolved', 'Timeout'];

const getHistoryDateSource = (item, isSpecialRole) => {
    if (isSpecialRole) {
        return item.confirmedOn || item.resolvedOn || item.respondedOn || item.notifiedOn || item.createdOn;
    }

    return item.notifiedOn || item.confirmedOn || item.respondedOn || item.resolvedOn || item.createdOn;
};

const HistoryTable = () => {
    const [alarmHistory, setAlarmHistory] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [filters, setFilters] = useState(defaultHistoryFilters);
    const [enabledAlarmOptions, setEnabledAlarmOptions] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const pageSize = 20;

    const roleId = parseInt(localStorage.getItem('roleId'), 10);
    const isSpecialRole = [5, 6, 7].includes(roleId);

    const fetchAlarmHistory = useCallback(async () => {
        try {
            setIsLoading(true);
            const { dateTimeFromUtc, dateTimeToUtc } = buildUtcDateTimeFilterRange(filters);
            const response = await axiosInstance.get('/AlarmHistory/get-alarm-histories', {
                params: {
                    pageNumber: currentPage,
                    pageSize,
                    patientReferenceNumber: filters.prn.trim() || null,
                    masterAlarmId: filters.alarmType === 'all' ? null : filters.alarmType,
                    dateFrom: dateTimeFromUtc || dateTimeToUtc ? null : filters.dateFrom || null,
                    dateTo: dateTimeFromUtc || dateTimeToUtc ? null : filters.dateTo || null,
                    timeFrom: dateTimeFromUtc || dateTimeToUtc ? null : filters.timeFrom || null,
                    timeTo: dateTimeFromUtc || dateTimeToUtc ? null : filters.timeTo || null,
                    dateTimeFromUtc,
                    dateTimeToUtc,
                    timeZoneOffsetMinutes: getClientTimezoneOffsetMinutes(),
                    location: filters.location.trim() || null,
                    status: filters.status === 'all' ? null : filters.status,
                }
            });
            
            if (response.data && response.data.result) {
                const historyData = (response.data.result.alarmHistoryList || []).map((item, index) => {
                    const dateSource = getHistoryDateSource(item, isSpecialRole);
                    let displayStatus = item.status;

                    if (isSpecialRole && item.status === 'Confirmed') {
                        displayStatus = 'Resolved';
                    }
                    
                    return {
                        ...item,
                        status: displayStatus,
                        serialNumber: (currentPage - 1) * pageSize + index + 1,
                        alarmType: masterAlarmMapping[item.masterAlarmId] || 'Unknown',
                        historyDateSource: dateSource,
                        date: formatApiDate(dateSource),
                        time: formatApiTime(dateSource),
                        location: item.department || '-'
                    };
                });

                setAlarmHistory(historyData);
                setTotalCount(response.data.result.totalCount || 0);
            } else {
                setAlarmHistory([]);
                setTotalCount(0);
            }
        } catch (error) {
            console.error('Error fetching alarm history:', error);
            setAlarmHistory([]);
            setTotalCount(0);
        } finally {
            setIsLoading(false);
        }
    }, [currentPage, filters, isSpecialRole]);

    const fetchEnabledAlarms = useCallback(async () => {
        try {
            const response = await axiosInstance.get('/alarm-settings/get');
            const alarmOptions = (response.data?.result || [])
                .filter((alarm) => alarm.masterAlarmId)
                .map((alarm) => ({
                    value: String(alarm.masterAlarmId),
                    label: masterAlarmMapping[alarm.masterAlarmId] || alarm.alarmType || 'Unknown'
                }))
                .sort((a, b) => a.label.localeCompare(b.label));

            setEnabledAlarmOptions(alarmOptions);
        } catch (error) {
            console.error('Error fetching enabled alarms:', error);
            setEnabledAlarmOptions([]);
        }
    }, []);

    useEffect(() => {
        fetchAlarmHistory();
    }, [fetchAlarmHistory]);

    useEffect(() => {
        fetchEnabledAlarms();
    }, [fetchEnabledAlarms]);

    const handleFilterChange = (nextFilters) => {
        setFilters(nextFilters);
        setCurrentPage(1);
    };

    const handleResetFilters = () => {
        setFilters({ ...defaultHistoryFilters });
        setCurrentPage(1);
    };

    const activeFilterCount = Object.entries(filters).filter(([key, value]) => (
        defaultHistoryFilters[key] !== value
    )).length;

    const totalPages = Math.ceil(totalCount / pageSize);
    const pageStartIndex = (currentPage - 1) * pageSize;

    const columns = useMemo(() => {
        const getStatusBadge = (status) => {
            const statusColors = {
                'Resolved': 'green', 'Timeout': 'tomato', 'Pending': 'yellow',
                'Confirmed': 'blue', 'Responded': 'orange'
            };
            const color = statusColors[status] || 'gray';
            return (
                <Badge color={color}>
                    <MdOutlineVerifiedUser />
                    {status}
                </Badge>
            );
        };

        const baseColumns = [
            { title: 'SN', key: 'serialNumber', render: (value) => <Text size={2}>{value}</Text> },
            { title: 'PRN', key: 'patientReferenceNumber', render: (value) => <Text size={2}>{value || '-'}</Text> },
        ];

        if (!isSpecialRole) {
            baseColumns.push({
                title: 'Alarm',
                key: 'alarmType',
                render: (value) => <Text size={2}>{value}</Text>,
            });
        }

        return [
            ...baseColumns,
            { title: 'Date', key: 'date', render: (value) => <Text size={2}>{value}</Text> },
            { title: 'Time', key: 'time', render: (value) => <Text size={2}>{value}</Text> },
            { title: 'Location', key: 'location', render: (value) => <Text size={2}>{value}</Text> },
            { title: 'Status', key: 'status', render: (value) => getStatusBadge(value) },
            {
                title: 'Action',
                key: 'actions',
                render: (value, row) => (
                    <ViewAlarmHistory
                        initialValues={row}
                        onHistoryUpdated={fetchAlarmHistory}
                        roleId={roleId}
                    />
                ),
            },
        ];
    }, [fetchAlarmHistory, isSpecialRole, roleId]);


    if (isLoading) {
        return (
            <Flex direction={'column'} gap={'3'}>
                <Flex justify="center" align="center" style={{ minHeight: '200px' }}>
                    <Text size={3}>Loading alarm history...</Text>
                </Flex>
            </Flex>
        );
    }

    return (
        <Flex direction={'column'} gap={'3'}>
            <Flex justify="end" align="center" gap="3" wrap="wrap">
                <Button
                    variant={isFilterOpen ? 'solid' : 'soft'}
                    size="2"
                    onClick={() => setIsFilterOpen((value) => !value)}
                >
                    <BiFilterAlt />
                    Filters
                    {activeFilterCount > 0 && (
                        <Badge color="blue" variant="soft">
                            {activeFilterCount}
                        </Badge>
                    )}
                </Button>
            </Flex>

            {isFilterOpen && (
                <HistoryFilter
                    filters={filters}
                    onFilterChange={handleFilterChange}
                    onReset={handleResetFilters}
                    alarmOptions={enabledAlarmOptions}
                    statusOptions={statusOptions}
                />
            )}

            {alarmHistory.length === 0 ? (
                <Flex justify="center" align="center" style={{ minHeight: '200px' }}>
                    <Text size={3} color="gray">No alarm history found</Text>
                </Flex>
            ) : (
                <>
                    <TableComponent dataStructure={alarmHistory} columns={columns} />
                    {totalPages > 1 && (
                        <PaginationComponent
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={(newPage) => setCurrentPage(newPage)}
                        />
                    )}
                    {totalCount > 0 && (
                        <Text size="2" align="center" color="gray" mt="1">
                            Showing {pageStartIndex + 1} - {Math.min(pageStartIndex + pageSize, totalCount)} of {totalCount} histories
                        </Text>
                    )}
                </>
            )}
        </Flex>
    );
};

export  default HistoryTable;
